import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "ui:chunk-load-error-reload-ts";
const RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Loading chunk",
  "Loading CSS chunk",
  "error loading dynamically imported module",
];

function isChunkLoadError(err: unknown): boolean {
  // A non-Error throw used to reach `String(err)`, which renders a plain object
  // as "[object Object]" — no pattern matches, so a genuine chunk-load failure
  // silently skipped the reload. Read `message` off anything that carries one.
  const message =
    err instanceof Error
      ? err.message
      : typeof err === "object" &&
          err !== null &&
          typeof (err as { message?: unknown }).message === "string"
        ? (err as { message: string }).message
        : typeof err === "string"
          ? err
          : "";
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function shouldReload(now: number = Date.now()): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY));
    if (!lastReload) return true;
    return now - lastReload > RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReload(now: number = Date.now()): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // sessionStorage may be unavailable (private browsing, etc.) — silently skip
  }
}

/**
 * Wraps React.lazy with automatic page reload on chunk load failures.
 * When a deploy invalidates the cached chunk hash, the dynamic import fails;
 * reloading fetches fresh HTML referencing the new chunk hashes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy itself uses ComponentType<any>; matching that signature keeps consumer call sites unannotated.
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err) && shouldReload()) {
        markReload();
        if (typeof window !== "undefined") {
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}

export const __internal = {
  isChunkLoadError,
  shouldReload,
  markReload,
  RELOAD_KEY,
  RELOAD_COOLDOWN_MS,
};
